// src/components/inventory/InventoryScanTab.tsx
// La merce entra da qui: si inquadra il codice a barre (o lo si digita).
// Codice già visto -> si somma la quantità; codice nuovo -> gli si dà un nome
// una volta sola e poi entra a magazzino. Entrambi i casi si aprono in un modale.
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanBarcode } from 'lucide-react';

import BarcodeScanner from '@/components/inventory/BarcodeScanner';
import InventoryStockEntryModal from '@/components/inventory/InventoryStockEntryModal';
import InventoryProductFormModal from '@/components/inventory/InventoryProductFormModal';
import { useInventory } from '@/contexts/InventoryContext';
import { InventoryProduct } from '@/services/inventory/inventoryService';

interface InventoryScanTabProps {
    onProductCreated?: (productId: number) => void;
}

type Pending =
    | { kind: 'known'; product: InventoryProduct }
    | { kind: 'new'; barcode: string };

const InventoryScanTab: React.FC<InventoryScanTabProps> = ({ onProductCreated }) => {
    const { t } = useTranslation();
    const { findByBarcode } = useInventory();

    const [manualCode, setManualCode] = useState('');
    // `pending` sopravvive alla chiusura, così il modale non si svuota mentre esce.
    const [pending, setPending] = useState<Pending | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Chiuso il modale la fotocamera riparte e vede ancora lo stesso codice:
    // per qualche secondo lo ignoriamo, altrimenti si riapre da solo.
    const lastCode = useRef<string | null>(null);
    const ignoreSameUntil = useRef(0);

    const handleCode = (code: string, fromCamera = false) => {
        const clean = code.trim();
        if (!clean) return;
        if (fromCamera && clean === lastCode.current && Date.now() < ignoreSameUntil.current) return;
        lastCode.current = clean;
        const existing = findByBarcode(clean);
        setPending(existing ? { kind: 'known', product: existing } : { kind: 'new', barcode: clean });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        ignoreSameUntil.current = Date.now() + 3000;
        setIsModalOpen(false);
    };

    return (
        <>
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 space-y-5">
                <p className="text-center text-sm font-semibold text-clip-600 flex items-center justify-center gap-2">
                    <ScanBarcode size={16} />
                    {t('inventory.scan.hint', 'Inquadra la merce che entra in magazzino')}
                </p>

                <BarcodeScanner onDetected={(code) => handleCode(code, true)} paused={isModalOpen} />

                <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm text-gray-500 mb-2">
                        {t('inventory.scan.manual', 'Oppure inserisci il codice a mano')}
                    </label>
                    <div className="flex gap-2">
                        <input
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCode(manualCode);
                                }
                            }}
                            inputMode="numeric"
                            placeholder="es. 8001841234561"
                            className="flex-1 min-w-0 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none"
                        />
                        <button
                            type="button"
                            onClick={() => handleCode(manualCode)}
                            disabled={!manualCode.trim()}
                            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            {t('common.search', 'Cerca')}
                        </button>
                    </div>
                </div>
            </div>

            <InventoryStockEntryModal
                isOpen={isModalOpen && pending?.kind === 'known'}
                onClose={closeModal}
                product={pending?.kind === 'known' ? pending.product : null}
                onRegistered={() => setManualCode('')}
            />

            <InventoryProductFormModal
                isOpen={isModalOpen && pending?.kind === 'new'}
                onClose={closeModal}
                barcode={pending?.kind === 'new' ? pending.barcode : null}
                onCreated={(id) => {
                    setManualCode('');
                    onProductCreated?.(id);
                }}
            />
        </>
    );
};

export default InventoryScanTab;
